"use client";

import { addDays, isSameMonth, startOfMonth, startOfToday } from "date-fns";
import { CalendarClock, Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import ErrorAlert from "@/src/components/error-alert";
import { Button } from "@/src/components/shadcn/ui/button";
import { Calendar } from "@/src/components/shadcn/ui/calendar";
import { Skeleton } from "@/src/components/shadcn/ui/skeleton";
import { dateFromKey, dateKeyFromDate, groupSlotsByDay, type NormalizedSlot } from "@/src/lib/scheduling/slot-display";
import { cn } from "@/src/lib/shadcn-utils";
import { formatAbsoluteTime, formatRelativeDateHeading } from "@/src/lib/utils/date-and-phone-utils";

const SELECTED_DAY_CLASSNAME =
  "[&>button]:bg-foreground [&>button]:text-background [&>button]:hover:bg-foreground [&>button]:hover:text-background";

export interface AppointmentSlotPickerProps {
  /** All currently-loaded slots (already fetched by the parent). */
  slots: NormalizedSlot[];
  selectedSlotId: string | null;
  /** Fired with the chosen slot, or `null` when the selection is cleared (e.g. day change). */
  onSelectSlot: (slot: NormalizedSlot | null) => void;
  isLoading?: boolean;
  error?: string | null;
  emptyMessage?: string;
  emptyHint?: string;
  /** Optional windowed search controls (calendar auto-widens as the user pages forward). */
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
  canLoadMore?: boolean;
  windowDays?: number;
  maxWindowDays?: number;
  /** Staff surfaces flag ranked slots; shows a subtle "Not preferred" hint. */
  showPreferredBadge?: boolean;
  /** Secondary label shown below each time. Provider is the default; provider-first flows use location. */
  slotSecondaryLabel?: "provider" | "location";
  /**
   * When the only-one-provider "Appointments with …" summary should render. Surfaces that offer their
   * own provider control (e.g. the online flow's provider dropdown) pass `false` to avoid duplication.
   */
  showProviderSummary?: boolean;
}

export function AppointmentSlotPicker({
  slots,
  selectedSlotId,
  onSelectSlot,
  isLoading = false,
  error = null,
  emptyMessage = "No online appointments in this window",
  emptyHint = "Try searching further out, or call the clinic to find a time that works.",
  onLoadMore,
  isLoadingMore = false,
  canLoadMore = false,
  windowDays,
  maxWindowDays,
  showPreferredBadge = false,
  slotSecondaryLabel = "provider",
  showProviderSummary = true,
}: AppointmentSlotPickerProps) {
  const today = useMemo(() => startOfToday(), []);
  const dayGroups = useMemo(() => groupSlotsByDay(slots), [slots]);
  const availableDateKeys = useMemo(() => new Set(dayGroups.map((group) => group.dateKey)), [dayGroups]);

  const distinctProviderCount = (() => {
    return new Set(slots.map((slot) => slot.providerDisplayName ?? "")).size;
  })();

  const lastAvailableDate = dayGroups.length > 0 ? dayGroups[dayGroups.length - 1].date : undefined;
  const maxSearchDate = maxWindowDays !== undefined ? addDays(today, maxWindowDays) : undefined;

  const [visibleMonth, setVisibleMonth] = useState<Date>(() => today);
  const [selectedDateKey, setSelectedDateKey] = useState<string | null>(null);

  // Keep the selected day (and the visible month) aligned with what's available: jump to the
  // first open day on load/reset, and to the first open day of a month the visitor pages into so
  // the slot list always matches the calendar.
  useEffect(() => {
    if (dayGroups.length === 0) {
      if (selectedDateKey !== null) {
        setSelectedDateKey(null);
      }

      return;
    }

    const selectedValidOverall = selectedDateKey !== null && availableDateKeys.has(selectedDateKey);

    if (!selectedValidOverall) {
      const first = dayGroups[0];

      setSelectedDateKey(first.dateKey);
      setVisibleMonth((prev) => (isSameMonth(prev, first.date) ? prev : startOfMonth(first.date)));

      return;
    }

    if (!isSameMonth(dateFromKey(selectedDateKey), visibleMonth)) {
      const firstInVisible = dayGroups.find((group) => isSameMonth(group.date, visibleMonth));

      if (firstInVisible) {
        setSelectedDateKey(firstInVisible.dateKey);
      }

      // Clear a slot selection whenever the visible month has no matching day — otherwise the
      // parent still holds a slot from the previous month and the user can book an invisible time.
      if (selectedSlotId) {
        onSelectSlot(null);
      }
    }
  }, [dayGroups, visibleMonth, availableDateKeys, selectedDateKey, selectedSlotId, onSelectSlot]);

  // When the visitor pages past the window we've already searched, widen it automatically.
  useEffect(() => {
    if (!canLoadMore || isLoadingMore || windowDays === undefined || !onLoadMore) {
      return;
    }

    const searchedThrough = addDays(today, windowDays);

    if (startOfMonth(visibleMonth).getTime() > searchedThrough.getTime()) {
      onLoadMore();
    }
  }, [visibleMonth, windowDays, canLoadMore, isLoadingMore, onLoadMore, today]);

  if (isLoading) {
    return (
      <div className="flex flex-wrap gap-6">
        <Skeleton className="h-72 w-full max-w-[320px] flex-[1_1_280px] rounded-lg" />
        <div className="min-w-[280px] flex-[1_1_280px] space-y-3">
          <Skeleton className="h-6 w-40" />
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
            {Array.from({ length: 8 }).map((_, index) => (
              <Skeleton key={index} className="h-10 w-full rounded-md" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return <ErrorAlert compact message={error} />;
  }

  if (dayGroups.length === 0) {
    return (
      <div className="flex flex-col items-center gap-4 rounded-lg border border-dashed p-8 text-center">
        <CalendarClock className="h-8 w-8 text-muted-foreground" />
        <div>
          <p className="font-medium text-foreground">{emptyMessage}</p>
          <p className="text-sm text-muted-foreground">{emptyHint}</p>
        </div>
        {canLoadMore && onLoadMore && (
          <Button variant="outline" onClick={onLoadMore} disabled={isLoadingMore}>
            {isLoadingMore && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Search later dates
          </Button>
        )}
      </div>
    );
  }

  const selectedDay = dayGroups.find((group) => group.dateKey === selectedDateKey) ?? null;
  const selectedInVisibleMonth = selectedDay ? isSameMonth(selectedDay.date, visibleMonth) : false;
  const visibleMonthLabel = visibleMonth.toLocaleDateString(undefined, { month: "long", year: "numeric" });

  const handleSelectDate = (dateKey: string) => {
    setSelectedDateKey(dateKey);

    if (selectedSlotId) {
      onSelectSlot(null);
    }
  };

  return (
    <div className="flex flex-wrap gap-6">
      <div className="relative mx-auto flex w-full max-w-[320px] flex-[1_1_280px] justify-center rounded-lg border bg-card p-2">
        <Calendar
          mode="single"
          month={visibleMonth}
          onMonthChange={setVisibleMonth}
          selected={selectedDateKey ? dateFromKey(selectedDateKey) : undefined}
          onSelect={(date) => date && handleSelectDate(dateKeyFromDate(date))}
          startMonth={today}
          endMonth={canLoadMore ? maxSearchDate : lastAvailableDate}
          disabled={(date) => date < today || !availableDateKeys.has(dateKeyFromDate(date))}
          selectedClassName={SELECTED_DAY_CLASSNAME}
          highlightToday
        />
        {isLoadingMore && (
          <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-background/60 backdrop-blur-[1px]">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}
      </div>

      <div className="min-w-[280px] flex-[1_1_280px] space-y-4">
        {showProviderSummary && distinctProviderCount === 1 && slots[0]?.providerDisplayName && (
          <p className="text-sm text-muted-foreground">
            Appointments with <span className="font-medium text-foreground">{slots[0].providerDisplayName}</span>
          </p>
        )}

        {selectedInVisibleMonth && selectedDay ? (
          <>
            <h3 className="text-base font-semibold text-foreground">
              {formatRelativeDateHeading(selectedDay.dateKey)}
            </h3>
            <div
              className={cn(
                "grid gap-2",
                slotSecondaryLabel === "location"
                  ? "grid-cols-[repeat(auto-fill,minmax(7.5rem,1fr))]"
                  : "grid-cols-[repeat(auto-fill,minmax(5rem,1fr))]"
              )}
            >
              {selectedDay.slots.map((slot) => {
                const isSelected = selectedSlotId === slot.id;
                const showNotPreferred = showPreferredBadge && slot.isPreferred === false;
                const secondaryLabel =
                  slotSecondaryLabel === "location"
                    ? slot.locationDisplayName
                    : distinctProviderCount > 1
                      ? slot.providerDisplayName
                      : null;

                return (
                  <button
                    key={slot.id}
                    type="button"
                    onClick={() => onSelectSlot(slot)}
                    aria-pressed={isSelected}
                    className={cn(
                      "flex flex-col items-center justify-center rounded-md border px-2 py-2 text-sm transition-all",
                      "hover:border-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                      isSelected
                        ? "border-foreground bg-foreground text-background"
                        : "border-border bg-background text-foreground"
                    )}
                  >
                    <span className="whitespace-nowrap font-medium">{formatAbsoluteTime(slot.start)}</span>
                    {secondaryLabel && (
                      <span
                        title={secondaryLabel}
                        className={cn(
                          "mt-0.5 max-w-full whitespace-normal break-words text-[11px]",
                          isSelected ? "text-background/80" : "text-muted-foreground"
                        )}
                      >
                        {secondaryLabel}
                      </span>
                    )}
                    {showNotPreferred && (
                      <span
                        className={cn(
                          "mt-0.5 text-[10px] uppercase tracking-wide",
                          isSelected ? "text-background/70" : "text-muted-foreground/70"
                        )}
                      >
                        Not preferred
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </>
        ) : isLoadingMore ? (
          <div className="flex items-center gap-2 rounded-lg border border-dashed p-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Finding more openings…
          </div>
        ) : (
          <div className="rounded-lg border border-dashed p-6 text-center">
            <p className="text-sm font-medium text-foreground">No openings in {visibleMonthLabel}</p>
            <p className="mt-1 text-sm text-muted-foreground">Use the calendar arrows to check another month.</p>
          </div>
        )}
      </div>
    </div>
  );
}
